import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase環境変数が設定されていません');
}

/**
 * セッションの保存キー（URLに依存しない固定値）。
 *
 * 背景: 旧実装は supabase-js の既定キー `sb-<ホスト先頭>-auth-token` を使っていた。
 * このキーは Supabase URL のホスト名から自動生成されるため、デプロイシステムが
 * `.env` の VITE_PUBLIC_SUPABASE_URL を書き換えてホスト表記が変わると、保存先キーが
 * ズレて「セッションが無い」状態になり、全端末・全ユーザーが一斉にログアウトしていた。
 * キーを固定値に切り離し、URL が変わってもセッションを見失わないようにする。
 */
export const AUTH_STORAGE_KEY = 'a1g-app-auth-token';

/**
 * localStorage に保存された値を読み出す。supabase-js は値が大きいとき
 * `<key>.0` / `<key>.1` … の分割チャンクで保存するため、その場合は連結して返す。
 */
export function readStoredValue(key: string): string | null {
  try {
    const base = localStorage.getItem(key);
    if (base !== null) return base;
    if (localStorage.getItem(`${key}.0`) === null) return null;
    let joined = '';
    for (let i = 0; ; i += 1) {
      const chunk = localStorage.getItem(`${key}.${i}`);
      if (chunk === null) break;
      joined += chunk;
    }
    return joined;
  } catch {
    return null;
  }
}

/** localStorage 上のキー本体と分割チャンクをまとめて削除する。 */
export function removeStoredGroup(key: string): void {
  try {
    localStorage.removeItem(key);
    for (let i = 0; ; i += 1) {
      const chunkKey = `${key}.${i}`;
      if (localStorage.getItem(chunkKey) === null) break;
      localStorage.removeItem(chunkKey);
    }
  } catch {
    // 失敗しても続行
  }
}

/**
 * 旧キー（`sb-<ホスト>-auth-token`）に残っているセッションを固定キーへ引き継ぐ。
 * 固定キーが空のときだけ実行する。これにより、保存キーを固定値へ切り替えても
 * 既存ユーザーはログアウトせずにそのまま使い続けられる。
 */
function migrateLegacyAuthSession(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (readStoredValue(AUTH_STORAGE_KEY) !== null) return;
    let legacyKey: string | null = null;
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        legacyKey = key;
        break;
      }
    }
    if (!legacyKey) return;
    const raw = readStoredValue(legacyKey);
    if (!raw) return;
    localStorage.setItem(AUTH_STORAGE_KEY, raw);
    // 旧キーは残す（別タブで旧ビルドが動いていても読み続けられるように）。
  } catch {
    // 移行できなくても致命的ではない
  }
}

// createClient より前に、旧キーから固定キーへセッションを引き継ぐ。
migrateLegacyAuthSession();

/**
 * 認証ロック（トークン更新の直列化）。
 *
 * Supabase はアクセストークンの更新をこのロックで「1つずつ順番に」実行する前提で動いている。
 * 更新が同時に2本走ると、リフレッシュトークンがローテーションで無効化され、片方が
 * "Invalid Refresh Token" で弾かれて意図しないサインアウト（＝突然のログアウト）を招く。
 * 特にモバイルでは、スリープ復帰直後や弱電波で更新が長引くため、この同時実行が起きやすい。
 *
 * 旧実装は「取得タイムアウト時にロック無しで fn() を実行」していたため、まさにこの同時実行を
 * 招いていた。新実装は二段構えにする:
 *  - Navigator LockManager があればクロスタブ排他を取る（タイムアウト付き）。
 *  - 取得できなくても「ロック無しで実行」はせず、必ずプロセス内の直列化キューを通す。
 */
const LOCK_TIMEOUT_MS = 15000;

/** プロセス内の直列化キュー（同じ name の処理を到着順に1つずつ実行する） */
const lockQueues = new Map<string, Promise<unknown>>();

function runSerialized<R>(name: string, fn: () => Promise<R>): Promise<R> {
  const prev = lockQueues.get(name) ?? Promise.resolve();
  const result = prev.then(fn);
  // 失敗しても後続を止めないよう、キューには「必ず解決する」Promise を残す
  lockQueues.set(name, result.then(() => undefined, () => undefined));
  return result;
}

const serializedLock = async <R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  // Navigator LockManager が無い環境はプロセス内の直列化のみで担保する
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return runSerialized(name, fn);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOCK_TIMEOUT_MS);

  try {
    return await navigator.locks.request(
      name,
      { mode: 'exclusive', signal: controller.signal },
      () => {
        // ロックを取得できたらタイムアウトは不要
        clearTimeout(timer);
        return runSerialized(name, fn);
      }
    );
  } catch {
    // 取得待ちでタイムアウト／中断した場合も、ロック無しでは実行せず直列化キューへ回す
    // （これが同時更新によるトークン失効＝ログアウトを防ぐ要）
    return runSerialized(name, fn);
  } finally {
    clearTimeout(timer);
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // トークンを自動更新する（これが無効だと期限切れトークンで
    // プロフィール取得やEdge Function呼び出しが401で失敗し、
    // ログインできない・管理操作ができない原因になる）
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    // 保存キーを URL から切り離す（デプロイでホスト表記が変わってもログアウトしない）
    storageKey: AUTH_STORAGE_KEY,
    lock: serializedLock,
  },
});

// 型定義
export interface Profile {
  id: string;
  username: string;
  furigana: string;
  email: string;
  role: 'welfare_user' | 'welfare_staff' | 'welfare_admin' | 'welfare_supervisor' | 'general_user' | 'general_admin';
  phone: string | null;
  office_id: string | null;
  departments: string[] | null;
  welfare_office_ids: string[] | null;
  /** 複数所属事業所ID（総合管理者・福祉事業管轄・掛け持ち職員で使用） */
  office_ids: string[] | null;
  /** アクティブ事業所ID（作業時の拠点。所属の中から選び、端末間で同期する） */
  active_office_id: string | null;
  /** 有効なアカウントか。false は「無効化（退職など）」で、記録・名前は残る。 */
  is_active?: boolean;
  /** IT管理者フラグ。ロール非依存（どのロールにも付与可・複数人可）。 */
  is_it_admin?: boolean;
  /** IT開発者フラグ。ロール非依存（どのロールにも付与可・複数人可）。 */
  is_it_developer?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Office {
  id: string;
  name: string;
  theme_color: string | null;
  created_at: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  target_role: 'all' | 'admin' | 'user';
  office_id: string | null;
  /** 配信対象の事業所ID（配列）。指定時は office_id より優先される（§7.2-1）。 */
  office_ids: string[] | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** 作成者プロフィール（結合取得）。作成者が削除済み／未設定なら null。 */
  profiles?: { username: string | null } | null;
}

export interface SupportRecord {
  id: string;
  user_id: string;
  title: string;
  content: string;
  /** 'plan'（個別支援計画） | 'case_note'（ケース記録） */
  record_type: 'plan' | 'case_note' | string;
  record_date: string;
  created_by: string | null;
  office_id: string | null;
  created_at: string;
  updated_at: string;
  /** ケース記録：天気 */
  weather?: string | null;
  /** ケース記録：利用時間（出勤・退勤） */
  work_start_time?: string | null;
  work_end_time?: string | null;
  /** ケース記録：客観的事実（本人の発言を含む） */
  facts?: string | null;
  /** ケース記録：所見・評価 */
  observations?: string | null;
  /** ケース記録：提供した支援と配慮 */
  support_provided?: string | null;
  /** ケース記録：作業状況・心身の状態・対人関係の細目（キーは constants/caseRecord.ts で定義） */
  case_details?: Record<string, string> | null;
}

export interface BusinessCard {
  id: string;
  name: string;
  furigana: string | null;
  company_name: string | null;
  position: string | null;
  department: string | null;
  office_phone: string | null;
  mobile_phone: string | null;
  fax: string | null;
  website: string | null;
  email: string | null;
  address: string | null;
  card_image_url: string | null;
  staff_in_charge: string | null;
  contact_date: string | null;
  notes: string | null;
  created_by: string | null;
  office_id: string | null;
  created_at: string;
  updated_at: string;
  /** 作成者プロフィール（結合取得）。作成者が削除済み／未設定なら null。 */
  profiles?: { username: string | null } | null;
}

// リトライ機能付きクエリ実行（改善版）
export async function queryWithRetry<T>(
  queryFn: () => Promise<{ data: T | null; error: any }>,
  maxRetries = 2,
  baseDelay = 500
): Promise<{ data: T | null; error: any }> {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      const result = await queryFn();

      if (!result.error) {
        return result;
      }

      lastError = result.error;

      // 認証エラーや権限エラーの場合はリトライしない
      if (
        result.error.code === 'PGRST301' ||
        result.error.message?.includes('JWT') ||
        result.error.message?.includes('permission')
      ) {
        return result;
      }

      // 最後の試行でない場合は待機
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, baseDelay * Math.pow(2, i)));
      }
    } catch (error: any) {
      lastError = error;

      if (error.name === 'AbortError') {
        console.error('クエリタイムアウト');
        return { data: null, error: new Error('リクエストがタイムアウトしました') };
      }

      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, baseDelay * Math.pow(2, i)));
      }
    }
  }

  return { data: null, error: lastError };
}

// 接続状態チェック
export async function checkConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.from('profiles').select('id').limit(1);
    return !error;
  } catch {
    return false;
  }
}